import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { FiCheck } from 'react-icons/fi';
import { GREEN, PRIMARY_COLOR, WHITE } from '../../../../appearance/Colors';
import LimitedOfferBanner from '../../../../components/LimitedOfferBanner/LimitedOfferBanner';
import { redirectToMobileStore } from '../../../../util/mobileStoreRedirect';

/**
 * SinglePlanPricing
 *
 * Used when there is exactly one paid plan tier on the landing page.
 *
 * Props:
 *   freePlan    – plan document with price === 0 (optional)
 *   monthlyPlan – plan document with type === 'monthly' (may be null)
 *   yearlyPlan  – plan document with type === 'yearly'  (may be null)
 *   getPaymentLinkWithPromo – fn(link) → string
 *
 * Layout (Todoist-inspired):
 *   - Billing toggle above the cards
 *   - CTA button first, price below it
 *   - Features list at bottom
 */
const SinglePlanPricing = ({ freePlan, monthlyPlan, yearlyPlan, getPaymentLinkWithPromo }) => {
    const hasMonthly = !!monthlyPlan;
    const hasYearly  = !!yearlyPlan;
    const isMobile   = useMediaQuery({ maxWidth: 767 });

    const [billingCycle, setBillingCycle] = useState(hasMonthly ? 'monthly' : 'yearly');

    const activePaid = billingCycle === 'yearly' ? (yearlyPlan || monthlyPlan) : (monthlyPlan || yearlyPlan);

    if (!activePaid) return null;

    // ── Helpers ────────────────────────────────────────────────────────────────

    const currencySymbol = activePaid.currency === 'eur' ? '€' : activePaid.currency === 'gbp' ? '£' : '$';

    const monthlyEquiv = (plan) => {
        if (!plan) return null;
        const raw = parseFloat(plan.price);
        return plan.type === 'yearly' ? Math.trunc((raw / 12) * 100) / 100 : raw;
    };

    const yearlySaving = (() => {
        if (!hasMonthly || !hasYearly) return null;
        const mo = parseFloat(monthlyPlan.price);
        const yr = parseFloat(yearlyPlan.price) / 12;
        if (!mo || mo <= 0) return null;
        return Math.round((1 - yr / mo) * 100);
    })();

    const getFeatures = (plan) => {
        if (!plan?.landingDescription) return [];
        try { return JSON.parse(plan.landingDescription); } catch { return []; }
    };

    const paidFeatures  = getFeatures(activePaid);
    const freeFeatures  = getFeatures(freePlan);

    // ── Billing toggle (rendered ABOVE cards) ─────────────────────────────────

    const BillingToggle = () => {
        if (!hasMonthly || !hasYearly) return null;
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', marginBottom: '28px' }}>
                <div style={{ position: 'relative' }}>
                    {/* Save callout — handwritten style above "Billed Yearly" */}
                    {yearlySaving > 0 && (
                        <div style={{
                            position: 'absolute', top: '-46px', left: '6px',
                            display: 'flex', alignItems: 'flex-end', gap: '2px',
                            pointerEvents: 'none', userSelect: 'none'
                        }}>
                            <span style={{
                                fontFamily: "'Caveat', 'Segoe Print', 'Bradley Hand', cursive",
                                fontSize: '26px', color: 'rgba(0,0,0,.45)', lineHeight: '1',
                                whiteSpace: 'nowrap', display: 'block', marginBottom: '4px'
                            }}>
                                Save {yearlySaving}%
                            </span>
                            {/* Curved arrow pointing down-right toward the toggle */}
                            <svg viewBox="0 0 54 54" fill="none" xmlns="http://www.w3.org/2000/svg"
                                width="44" height="36" style={{ transform: 'scaleX(-1)', flexShrink: 0 }}>
                                <path d="M18.7 1.2C16 21 21.3 39.3 34.2 43.4"
                                    stroke="rgba(0,0,0,.35)" strokeWidth="2" strokeLinecap="round" />
                                <path d="m36.3 38.8-.2.6c0 .2.1.5.4.8.4.8.8 1.7 1 2.6.4 1 .6 2 .7 2.8v1.8l-2 .1a18 18 0 0 1-4.8-.8 3 3 0 0 0-.8-.2c-.2 0-.4.1-.5.3a1 1 0 0 0-.3.7c0 .3.1.5.4.7l.3.2.7.3a16.9 16.9 0 0 0 3.6.6 29.9 29.9 0 0 0 4 0h.6a1.5 1.5 0 0 0 .5-.6l.2-.6a13.3 13.3 0 0 0-.1-4 17 17 0 0 0-1.9-5c0-.2-.2-.3-.3-.4a1 1 0 0 0-.7-.3c-.3 0-.5.1-.8.4Z"
                                    fill="rgba(0,0,0,.35)" />
                            </svg>
                        </div>
                    )}

                    <div style={{
                        display: 'flex', backgroundColor: 'rgba(0,0,0,.07)',
                        borderRadius: '8px', padding: '4px', gap: '4px'
                    }}>
                        {[{ key: 'yearly', label: 'Billed Yearly' }, { key: 'monthly', label: 'Billed Monthly' }].map(({ key, label }) => {
                            const active = billingCycle === key;
                            return (
                                <button key={key} onClick={() => setBillingCycle(key)} style={{
                                    padding: '7px 18px', borderRadius: '6px', border: 'none',
                                    backgroundColor: active ? 'white' : 'transparent',
                                    cursor: 'pointer',
                                    boxShadow: active ? '0 1px 4px rgba(0,0,0,.12)' : 'none',
                                    transition: 'all 0.2s ease'
                                }}>
                                    <span style={{ fontSize: '13px', fontWeight: active ? '600' : '400', color: PRIMARY_COLOR }}>
                                        {label}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        );
    };

    // ── Feature row ────────────────────────────────────────────────────────────

    const FeatureRow = ({ feature }) => (
        <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 10 }}>
            <div style={{ marginRight: 10, marginTop: 3, flexShrink: 0 }}>
                <div style={{
                    backgroundColor: GREEN, width: 17, height: 17, borderRadius: '50%',
                    display: 'flex', justifyContent: 'center', alignItems: 'center'
                }}>
                    <FiCheck size={11} color={WHITE} />
                </div>
            </div>
            <div style={{ fontSize: 14, lineHeight: '22px', flex: 1, fontWeight: '500', color: PRIMARY_COLOR }}>
                {feature.title}
                {feature.description && (
                    <div style={{ fontSize: 13, lineHeight: '18px', opacity: 0.6, fontWeight: 'normal' }}>
                        {feature.description}
                    </div>
                )}
            </div>
        </div>
    );

    // ── Card shell ─────────────────────────────────────────────────────────────

    const cardStyle = {
        background: 'white', borderRadius: '16px', padding: '28px 24px',
        display: 'flex', flexDirection: 'column',
        border: '1px solid rgba(20, 17, 15, 0.08)',
        position: 'relative', overflow: 'visible'
    };

    // ── CTA button ─────────────────────────────────────────────────────────────

    const onOver = (e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.9'; };
    const onOut  = (e) => { e.currentTarget.style.transform = 'translateY(0)';    e.currentTarget.style.opacity = '1'; };

    const btnBase = {
        display: 'block', width: '100%', padding: '14px 24px',
        borderRadius: '8px', fontSize: '15px', fontWeight: '600',
        textAlign: 'center', textDecoration: 'none', border: 'none',
        transition: 'all 0.2s ease', cursor: 'pointer', boxSizing: 'border-box'
    };

    const PaidCta = () => {
        if (activePaid.stripePaymentLink) {
            return (
                <a href={getPaymentLinkWithPromo(activePaid.stripePaymentLink)}
                    target="_blank" rel="noopener noreferrer"
                    style={{ ...btnBase, backgroundColor: GREEN, color: 'white', boxShadow: '0 4px 12px rgba(0,137,111,.3)' }}
                    onMouseOver={onOver} onMouseOut={onOut}>
                    Start free
                </a>
            );
        }
        return (
            <Link to="/register" onClick={redirectToMobileStore}
                style={{ ...btnBase, backgroundColor: GREEN, color: 'white', boxShadow: '0 4px 12px rgba(0,137,111,.3)' }}
                onMouseOver={onOver} onMouseOut={onOut}>
                Start free
            </Link>
        );
    };

    // ── Divider ────────────────────────────────────────────────────────────────

    const Divider = ({ label }) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '20px 0 16px' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,.08)' }} />
            {label && (
                <span style={{ fontSize: '12px', color: PRIMARY_COLOR, opacity: 0.45, fontWeight: '500', whiteSpace: 'nowrap' }}>
                    {label}
                </span>
            )}
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(0,0,0,.08)' }} />
        </div>
    );

    // ── Two-column layout (with free plan) ────────────────────────────────────

    if (freePlan) {
        return (
            <div style={{ width: '100%', maxWidth: '820px', margin: '0 auto' }}>
                {/* Toggle sits above both cards */}
                <BillingToggle />

                <div style={{
                    display: 'flex',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '20px',
                    alignItems: 'stretch',
                }}>
                    {/* ── Free column ── */}
                    <div style={{ flex: isMobile ? 'none' : '1', minWidth: 0, order: isMobile ? 2 : 1 }}>
                        <div style={{ ...cardStyle, height: '100%' }}>
                            {/* Name */}
                            <h3 style={{ margin: '0 0 20px', fontSize: '24px', fontWeight: '700', color: PRIMARY_COLOR }}>
                                {freePlan.name || 'Free'}
                            </h3>

                            {/* CTA */}
                            <Link to="/register" onClick={redirectToMobileStore} style={{
                                ...btnBase,
                                backgroundColor: 'transparent', color: PRIMARY_COLOR,
                                border: `2px solid rgba(0,0,0,.15)`,
                            }}
                                onMouseOver={(e) => { e.currentTarget.style.borderColor = 'rgba(0,0,0,.3)'; e.currentTarget.style.backgroundColor = 'rgba(0,0,0,.03)'; }}
                                onMouseOut={(e)  => { e.currentTarget.style.borderColor = 'rgba(0,0,0,.15)'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                                Start free
                            </Link>

                            {/* Price */}
                            <div style={{ marginTop: '20px' }}>
                                <div style={{ fontSize: 34, fontWeight: 'bold', color: PRIMARY_COLOR, lineHeight: '1' }}>
                                    {currencySymbol}0
                                </div>
                                <div style={{ fontSize: 13, opacity: 0.5, marginTop: '6px' }}>Free forever</div>
                            </div>

                            {/* Features */}
                            {freeFeatures.length > 0 && (
                                <>
                                    <Divider />
                                    <div style={{ flex: 1 }}>
                                        {freeFeatures.map((f, i) => <FeatureRow key={i} feature={f} />)}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* ── Paid column ── */}
                    <div style={{ flex: isMobile ? 'none' : '1', minWidth: 0, order: isMobile ? 1 : 2 }}>
                        <div style={{ ...cardStyle, height: '100%' }}>
                            {/* Tag */}
                            {activePaid.tag && (
                                <div style={{ position: 'absolute', top: '-12px', right: '20px', zIndex: 10 }}>
                                    <span style={{
                                        fontSize: '12px', fontWeight: '600', color: '#6F5A1F',
                                        backgroundColor: '#FFF6DA', border: '1px solid rgba(250,230,164,1)',
                                        padding: '5px 12px', borderRadius: '20px', display: 'inline-block',
                                        boxShadow: '0 1px 4px rgba(0,0,0,.12)', whiteSpace: 'nowrap'
                                    }}>{activePaid.tag}</span>
                                </div>
                            )}

                            {activePaid.limitedOffer?.isActive && (
                                <LimitedOfferBanner text={activePaid.limitedOffer.text} endDate={activePaid.limitedOffer.endDate} position="top" showTimer={true} />
                            )}

                            {/* Name */}
                            <h3 style={{ margin: '0 0 20px', fontSize: '24px', fontWeight: '700', color: PRIMARY_COLOR }}>
                                {activePaid.name}
                            </h3>

                            {/* CTA */}
                            <PaidCta />

                            {/* Price */}
                            <div style={{ marginTop: '20px' }}>
                                {billingCycle === 'yearly' && yearlyPlan ? (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                            <span style={{ fontSize: 34, fontWeight: 'bold', color: PRIMARY_COLOR, lineHeight: '1' }}>
                                                {currencySymbol}{Math.trunc(parseFloat(yearlyPlan.price) * 100) / 100}
                                            </span>
                                            <span style={{ fontSize: 14, color: PRIMARY_COLOR, opacity: 0.55 }}>/ year</span>
                                        </div>
                                        <div style={{ fontSize: 13, opacity: 0.5, marginTop: '6px' }}>
                                            that's {currencySymbol}{monthlyEquiv(activePaid)} per month
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                            <span style={{ fontSize: 34, fontWeight: 'bold', color: PRIMARY_COLOR, lineHeight: '1' }}>
                                                {currencySymbol}{monthlyEquiv(activePaid)}
                                            </span>
                                            <span style={{ fontSize: 14, color: PRIMARY_COLOR, opacity: 0.55 }}>/ month</span>
                                        </div>
                                        <div style={{ fontSize: 13, opacity: 0.5, marginTop: '6px' }}>Billed monthly</div>
                                    </>
                                )}
                            </div>

                            {/* Features */}
                            {paidFeatures.length > 0 && (
                                <>
                                    <Divider />
                                    <div style={{ flex: 1 }}>
                                        {paidFeatures.map((f, i) => <FeatureRow key={i} feature={f} />)}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── Single centred card (no free plan) ────────────────────────────────────

    return (
        <div style={{ width: '100%', maxWidth: '440px', margin: '0 auto' }}>
            <BillingToggle />
            <div style={cardStyle}>
                {activePaid.tag && (
                    <div style={{ position: 'absolute', top: '-12px', right: '20px', zIndex: 10 }}>
                        <span style={{
                            fontSize: '12px', fontWeight: '600', color: '#6F5A1F',
                            backgroundColor: '#FFF6DA', border: '1px solid rgba(250,230,164,1)',
                            padding: '5px 12px', borderRadius: '20px', display: 'inline-block',
                            boxShadow: '0 1px 4px rgba(0,0,0,.12)'
                        }}>{activePaid.tag}</span>
                    </div>
                )}

                {activePaid.limitedOffer?.isActive && (
                    <LimitedOfferBanner text={activePaid.limitedOffer.text} endDate={activePaid.limitedOffer.endDate} position="top" showTimer={true} />
                )}

                <h3 style={{ margin: '0 0 20px', fontSize: '24px', fontWeight: '700', color: PRIMARY_COLOR }}>
                    {activePaid.name}
                </h3>

                <PaidCta />

                <div style={{ marginTop: '20px' }}>
                    {billingCycle === 'yearly' && yearlyPlan ? (
                        <>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span style={{ fontSize: 36, fontWeight: 'bold', color: PRIMARY_COLOR, lineHeight: '1' }}>
                                    {currencySymbol}{Math.trunc(parseFloat(yearlyPlan.price) * 100) / 100}
                                </span>
                                <span style={{ fontSize: 14, color: PRIMARY_COLOR, opacity: 0.55 }}>/ year</span>
                            </div>
                            <div style={{ fontSize: 13, opacity: 0.5, marginTop: '6px' }}>
                                that's {currencySymbol}{monthlyEquiv(activePaid)} per month
                            </div>
                        </>
                    ) : (
                        <>
                            <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                <span style={{ fontSize: 36, fontWeight: 'bold', color: PRIMARY_COLOR, lineHeight: '1' }}>
                                    {currencySymbol}{monthlyEquiv(activePaid)}
                                </span>
                                <span style={{ fontSize: 14, color: PRIMARY_COLOR, opacity: 0.55 }}>/ month</span>
                            </div>
                            <div style={{ fontSize: 13, opacity: 0.5, marginTop: '6px' }}>Billed monthly</div>
                        </>
                    )}
                </div>

                {paidFeatures.length > 0 && (
                    <>
                        <Divider />
                        <div>
                            {paidFeatures.map((f, i) => <FeatureRow key={i} feature={f} />)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SinglePlanPricing;
